import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";
import chinaGeoJson from "../assets/china.geo.json";

echarts.registerMap("china", chinaGeoJson);

const cityPoints = [
  { name: "Beijing", value: [116.4074, 39.9042], labelPos: "right", labelOffset: [10, -12] },
  { name: "Shanghai", value: [121.4737, 31.2304], labelPos: "right", labelOffset: [12, -14] },
  { name: "Hangzhou", value: [120.1551, 30.2741], labelPos: "left", labelOffset: [-14, -2] },
  { name: "Shenzhen", value: [114.0579, 22.5431], labelPos: "right", labelOffset: [12, 16] },
  { name: "Guangzhou", value: [113.2644, 23.1291], labelPos: "right", labelOffset: [18, -30], hideMainLabel: true },
  { name: "Chengdu", value: [104.0665, 30.5728], labelPos: "left", labelOffset: [-12, -10] },
  { name: "Xi'an", value: [108.9398, 34.3416], labelPos: "left", labelOffset: [-12, -10] },
  { name: "Guilin", value: [110.2991, 25.2742], labelPos: "left", labelOffset: [-14, -14] },
  { name: "Sanya", value: [109.5119, 18.2528], labelPos: "right", labelOffset: [10, 12] },
];

const routePairs = [
  ["Beijing", "Shanghai"],
  ["Beijing", "Xi'an"],
  ["Xi'an", "Chengdu"],
  ["Chengdu", "Guilin"],
  ["Guilin", "Guangzhou"],
  ["Guangzhou", "Shenzhen"],
  ["Shanghai", "Hangzhou"],
  ["Hangzhou", "Shenzhen"],
  ["Guilin", "Sanya"],
];

const pointByName = Object.fromEntries(cityPoints.map((p) => [p.name, p.value]));
const routeData = routePairs.map(([from, to]) => ({
  fromName: from,
  toName: to,
  coords: [pointByName[from], pointByName[to]],
}));
const guangzhouCoord = [113.2644, 23.1291];
const guangzhouLabelCoord = [113.95, 24.58];

const DEFAULT_MAP_SETTINGS = {
  zoom: 1.5,
  centerX: 106.8,
  centerY: 32.5,
  layoutX: 52,
  layoutY: 50,
  layoutSize: 118,
  cityLabelSize: 14,
  cityLabelColor: "#6fd6ff",
  cityMarkerSize: 8,
  cityMarkerColor: "#ff6b35",
};

const toNum = (value, fallback) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

export const ChinaMap = ({ className = "", mapSettings }) => {
  const cityLabelColor = mapSettings?.cityLabelColor || DEFAULT_MAP_SETTINGS.cityLabelColor;
  const cityMarkerColor = mapSettings?.cityMarkerColor || DEFAULT_MAP_SETTINGS.cityMarkerColor;
  const settings = {
    zoom: toNum(mapSettings?.zoom, DEFAULT_MAP_SETTINGS.zoom),
    centerX: toNum(mapSettings?.centerX, DEFAULT_MAP_SETTINGS.centerX),
    centerY: toNum(mapSettings?.centerY, DEFAULT_MAP_SETTINGS.centerY),
    layoutX: toNum(mapSettings?.layoutX, DEFAULT_MAP_SETTINGS.layoutX),
    layoutY: toNum(mapSettings?.layoutY, DEFAULT_MAP_SETTINGS.layoutY),
    layoutSize: toNum(mapSettings?.layoutSize, DEFAULT_MAP_SETTINGS.layoutSize),
    cityLabelSize: toNum(mapSettings?.cityLabelSize, DEFAULT_MAP_SETTINGS.cityLabelSize),
    cityMarkerSize: toNum(mapSettings?.cityMarkerSize, DEFAULT_MAP_SETTINGS.cityMarkerSize),
  };

  const option = {
    backgroundColor: "transparent",
    tooltip: { show: false },
    geo: {
      map: "china",
      roam: false,
      silent: true,
      zoom: settings.zoom,
      center: [settings.centerX, settings.centerY],
      layoutCenter: [`${settings.layoutX}%`, `${settings.layoutY}%`],
      layoutSize: `${settings.layoutSize}%`,
      label: { show: false },
      itemStyle: {
        areaColor: "#182846",
        borderColor: "rgba(142,177,255,0.82)",
        borderWidth: 0.9,
      },
      emphasis: { disabled: true },
    },
    series: [
      {
        name: "Routes",
        type: "lines",
        coordinateSystem: "geo",
        zlevel: 2,
        effect: {
          show: true,
          period: 4,
          trailLength: 0.26,
          symbol: "circle",
          symbolSize: 5,
          color: "#7ef7ff",
        },
        lineStyle: {
          color: "#3de7ff",
          width: 1.5,
          opacity: 0.85,
          curveness: 0.2,
        },
        tooltip: {
          show: true,
          trigger: "item",
          backgroundColor: "rgba(10,14,28,0.96)",
          borderColor: "rgba(120,220,255,0.65)",
          borderWidth: 1,
          textStyle: { color: "#d9f4ff", fontSize: 11 },
          formatter: (p) => `${p.data?.fromName || ""} → ${p.data?.toName || ""}`,
        },
        data: routeData,
      },
      {
        name: "Cities",
        type: "effectScatter",
        coordinateSystem: "geo",
        zlevel: 3,
        rippleEffect: { brushType: "stroke", scale: 3.2 },
        symbolSize: settings.cityMarkerSize,
        itemStyle: { color: cityMarkerColor },
        label: {
          show: true,
          formatter: (params) => (params?.name === "Guangzhou" ? "" : params.name),
          position: "right",
          distance: 6,
          color: cityLabelColor,
          fontSize: settings.cityLabelSize,
          fontWeight: 600,
          textShadowColor: "rgba(59, 210, 255, 0.95)",
          textShadowBlur: 10,
        },
        labelLayout: {
          moveOverlap: "shiftY",
          hideOverlap: false,
        },
        tooltip: {
          show: true,
          trigger: "item",
          backgroundColor: "rgba(10,14,28,0.96)",
          borderColor: "rgba(120,220,255,0.65)",
          borderWidth: 1,
          textStyle: { color: "#d9f4ff", fontSize: 11 },
          formatter: (p) => p.name,
        },
        emphasis: { scale: true },
        data: cityPoints.map((p) => ({
          name: p.name,
          value: p.value,
          label: {
            position: p.labelPos || "right",
            offset: p.labelOffset || [0, 0],
          },
        })),
      },
      {
        name: "Guangzhou label connector",
        type: "lines",
        coordinateSystem: "geo",
        zlevel: 4,
        effect: { show: false },
        lineStyle: {
          color: "#6fd6ff",
          width: 1.6,
          opacity: 0.95,
          curveness: 0,
        },
        silent: true,
        data: [{ coords: [guangzhouCoord, guangzhouLabelCoord] }],
      },
      {
        name: "Guangzhou label",
        type: "scatter",
        coordinateSystem: "geo",
        zlevel: 5,
        symbolSize: 1,
        itemStyle: { color: "rgba(0,0,0,0)" },
        silent: true,
        label: {
          show: true,
          formatter: "Guangzhou",
          position: "right",
          offset: [6, -4],
          color: cityLabelColor,
          fontSize: settings.cityLabelSize,
          fontWeight: 600,
          textShadowColor: "rgba(59, 210, 255, 0.95)",
          textShadowBlur: 10,
        },
        tooltip: { show: false },
        data: [{ name: "Guangzhou-label", value: guangzhouLabelCoord }],
      },
    ],
  };

  return (
    <div className={className} data-testid="china-map">
      <ReactECharts
        echarts={echarts}
        option={option}
        notMerge
        lazyUpdate
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  );
};
